import type { AccountStateReason } from '@/types/generated/schemas';

export type SessionEndReason = Extract<
  AccountStateReason,
  'ACCOUNT_INACTIVE' | 'ORGANIZATION_CHANGED' | 'TOKEN_REVOKED' | 'REAUTHENTICATE' | 'ACCOUNT_CONFLICT'
>;

export const sessionEndMessages: Record<SessionEndReason, string> = {
  ACCOUNT_INACTIVE: 'Your account is inactive. Please contact your organization.',
  ORGANIZATION_CHANGED: 'Your organization changed. Please sign in again.',
  TOKEN_REVOKED: 'Your session was revoked. Please sign in again.',
  REAUTHENTICATE: 'Please sign in again.',
  ACCOUNT_CONFLICT: 'Your account needs support. Please contact support.',
};

export const isSessionEndReason = (reason: unknown): reason is SessionEndReason =>
  typeof reason === 'string' && Object.prototype.hasOwnProperty.call(sessionEndMessages, reason);

export const accountStateUnavailable = 'ACCOUNT_STATE_UNAVAILABLE' satisfies AccountStateReason;
export const accountOperationInProgress = 'ACCOUNT_OPERATION_IN_PROGRESS' satisfies AccountStateReason;
export const maintenanceFreeze = 'MAINTENANCE_FREEZE' satisfies AccountStateReason;

export const ACCOUNT_SESSION_MESSAGE_KEY = 'omnia-account-session-message';
export const ACCOUNT_STATE_EVENT = 'omnia-account-state-error';

export const notifyAccountState = (message: string) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ACCOUNT_STATE_EVENT, { detail: message }));
};

export const setupAccountStateMessage = (error: unknown, setupPhase?: 'verify' | 'submit'): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const response = error as {
    code?: unknown;
    reason?: unknown;
    message?: unknown;
    errors?: unknown;
    maintenanceWindowEnd?: unknown;
    maintenanceEndAt?: unknown;
    plannedEndAt?: unknown;
    data?: {
      maintenanceWindowEnd?: unknown;
      maintenanceEndAt?: unknown;
      plannedEndAt?: unknown;
      details?: { maintenanceWindowEnd?: unknown; maintenanceEndAt?: unknown; plannedEndAt?: unknown };
    };
  };
  if (response.reason === accountOperationInProgress)
    return 'Account setup is in progress. Please try again in a moment.';
  if (response.reason === accountStateUnavailable) return 'Account service is unavailable. Please try again.';
  if (response.reason === maintenanceFreeze) {
    const end =
      response.maintenanceWindowEnd ??
      response.maintenanceEndAt ??
      response.plannedEndAt ??
      response.data?.maintenanceWindowEnd ??
      response.data?.maintenanceEndAt ??
      response.data?.plannedEndAt ??
      response.data?.details?.maintenanceWindowEnd ??
      response.data?.details?.maintenanceEndAt ??
      response.data?.details?.plannedEndAt;
    const endDate = typeof end === 'string' ? new Date(end) : null;
    const endText = endDate && !Number.isNaN(endDate.getTime()) ? ` Planned end: ${endDate.toLocaleString()}.` : '';
    return `Invitations can be accepted again after maintenance.${endText}`;
  }

  if (!setupPhase) return undefined;
  if (response.code === 429)
    return setupPhase === 'verify'
      ? 'Too many attempts. Please wait a minute and reload this page.'
      : 'Too many attempts. Please wait a minute and try again.';

  const firstError = Array.isArray(response.errors) ? response.errors[0] : undefined;
  const errorMessage =
    firstError && typeof firstError === 'object' && 'message' in firstError ? firstError.message : firstError;
  const message =
    typeof errorMessage === 'string' && errorMessage.trim()
      ? errorMessage
      : typeof response.message === 'string' && response.message.trim()
        ? response.message
        : undefined;

  switch (response.reason) {
    case 'LINK_EXPIRED':
      return 'This invitation has expired. Please ask your administrator for a new invitation.';
    case 'LINK_INVALID':
      return 'This invitation link is invalid. Please ask your administrator for a new invitation.';
    case 'ORGANIZATION_INACTIVE':
      return 'This invitation cannot be used right now because the organization is inactive. Please contact your administrator.';
    case 'SETUP_IN_PROGRESS':
      return 'Account setup is in progress. Please try again later.';
    default:
      return typeof response.reason === 'string' && response.reason ? message : undefined;
  }
};
